/**
 * Shallow-equal prop comparison for React.memo that ignores function-typed props.
 *
 * The coach client-detail tabs (ClientProgramsTab, ClientPerformanceTab, etc.)
 * each receive 40-60 props from ClientManagement.jsx — a ~2,600-line component
 * with ~75 useState values. ~20 of those props are handler functions defined in
 * the component body, so they get a fresh identity on every render. With a plain
 * React.memo (or none), a single keystroke in any unrelated field on the parent
 * re-renders the entire 78 KB ClientProgramsTab / 44 KB ClientPerformanceTab
 * subtree, redrawing every exercise row and chart. That's the source of the
 * "[Violation] 'handler' took NNNms" main-thread stalls.
 *
 * This comparator skips function identity and shallow-compares everything else.
 * It's safe here because every handler closes over state that is ALSO passed
 * down as a data prop, so whenever a handler's behaviour would actually change,
 * one of the non-function props changes too and this returns false (re-render).
 *
 * Known minor exception: logCoachingDecision (called by swap/delete handlers)
 * also reads coachAuthId and performanceAnalytics, which are not passed to
 * ClientProgramsTab. coachAuthId is set once per session; performanceAnalytics
 * only feeds a non-critical `completion_rate` value into the audit-log context
 * JSON. A stale close-over there is cosmetic, not a user-facing bug.
 *
 * Returns true when props are considered equal (skip re-render).
 */
export const shallowEqualIgnoringFunctions = (prevProps, nextProps) => {
  const prevKeys = Object.keys(prevProps);
  const nextKeys = Object.keys(nextProps);
  if (prevKeys.length !== nextKeys.length) return false;

  for (const key of prevKeys) {
    const prev = prevProps[key];
    const next = nextProps[key];
    if (typeof prev === 'function' && typeof next === 'function') continue;
    if (!Object.is(prev, next)) return false;
  }
  return true;
};
